import type { ECElementEvent } from 'echarts';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { isNumber } from 'lodash-es';
import type { JSX } from 'react';

import { useStableChartOption } from './StableChartOption';
import { ChartAxis, ChartSeries, axisColor, chartColor, chartFont } from './charts.common';

/**
 * @param yAxisInfo https://echarts.apache.org/en/option.html#yAxis
 */
export interface BarChartProps {
    /** The function to call when an arc is clicked. Will be called with the `name` of an element from `data`. */
    onChartClick: (item: string) => void;
    xAxisLabels: string[];
    /**
     * The y-axes for the chart. This should contain one element with a 'left' `position` if there is no `line`; or two elements with `positions` 'left' and 'right, respectively, if there is a `line`.
     * The left y-axis always corresponds with the bars and the right y-axis always corresponds with the line.
     *
     * IMPORTANT: If `line` is present, the `name` of the second element should match the `name` of the `line`. (No, there does not seem to be a good reason for these separate definitions.)
     */
    yAxisInfo: ChartAxis[];
    /** The values of the bars. Each element in `bars.data` represents the value for the bar for the same index of `xAxisLabels`. */
    bars: ChartSeries;
    /**
     * An optional line chart to superimpose over the bars. When the user hovers over a point on the line, the user will see the `name` and `value` from this prop, as well as the corresponding label from `xAxisLabels`.
     *
     * IMPORTANT: The `name` of this line should match the `name` of the second element of `yAxisInfo`. (No, there does not seem to be a good reason for these separate definitions.)
     */
    line?: ChartSeries;
}

/**
 * A bar chart with vertical bars, optionally with a line chart superimposed.
 * Note that as of the time of writing, a `line` is never actually passed into this component, so that prop may or may not function correctly.
 */
export const BarChart = (props: BarChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);
    const labelColor = getComputedStyle(document.documentElement).getPropertyValue(chartColor);

    const ariaDescription = (() => {
        const getLineValueDescription = (index: number): string => {
            const dataPoint = props.line?.data[index];
            if (dataPoint === undefined) {
                return '';
            }
            const valueAsNumber = isNumber(dataPoint) ? dataPoint : dataPoint.value;
            return `, and the value of the line is ${valueAsNumber}`;
        };

        const valuesDescriptions = props.bars.data.map((element, index) => `For ${props.xAxisLabels[index]}, the value of the bar is ${isNumber(element) ? element : element.value}${getLineValueDescription(index)}.`).join(' ');
        const chartType = props.line ? `A bar chart showing ${props.yAxisInfo[0].name}, with a superimposed line chart showing ${props.yAxisInfo[1].name}.` : `A bar chart showing ${props.yAxisInfo[0].name}.`;
        return `${chartType} ${valuesDescriptions}`;
    })();

    const getOption = (info: Omit<BarChartProps, 'onChartClick'>, primaryFont: string, labelColor: string): EChartsOption => {
        const yAxes: ChartAxis[] = info.yAxisInfo.length < 2 ? [...info.yAxisInfo, { name: '', position: 'right' }] : info.yAxisInfo;

        return {
            tooltip: {
                trigger: 'item',
            },

            legend: { show: false },
            textStyle: {
                fontFamily: primaryFont,
            },
            grid: {
                containLabel: true,
                left: '10%',
                right: '10%',
                top: '10%',
                bottom: '10%',
            },
            xAxis: [
                {
                    type: 'category',
                    axisTick: {
                        alignWithLabel: true,
                    },
                    data: info.xAxisLabels.map((label) => label.toUpperCase()),
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: axisColor,
                        },
                    },
                    axisLabel: {
                        color: labelColor,
                    },
                },
            ],
            yAxis: yAxes.map((yAxis) => ({
                type: 'value',
                name: yAxis.name?.toUpperCase(),
                position: yAxis.position,
                min: yAxis.min,
                max: yAxis.max,
                minInterval: yAxis.minInterval,
                maxInterval: yAxis.maxInterval,
                interval: yAxis.interval,
                alignTicks: true,
                show: true,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisColor,
                    },
                },
                axisLabel: {
                    formatter: yAxis.labelFormatter,
                    color: labelColor,
                },
                nameLocation: 'middle',
                nameGap: 50,
                nameRotate: yAxis.position === 'left' ? 90 : 270,
                splitLine: {
                    lineStyle: {
                        color: axisColor,
                    },
                },
                nameTextStyle: {
                    color: labelColor,
                },
            })),
            series: [
                {
                    name: info.bars.name,
                    type: 'bar',
                    itemStyle: { borderRadius: [50, 50, 0, 0], color: info.bars.color },
                    data: [...info.bars.data],
                    barWidth: '10%',
                },
            ],
        };
    };

    const option = useStableChartOption(getOption(props, primaryFont, labelColor));

    return (
        <ReactECharts
            aria-label={ariaDescription}
            role="img"
            option={option}
            style={{
                height: '100%',
                width: '100%',
                minHeight: '300px',
                minWidth: '200px',
                maxHeight: '300px',
            }}
            onEvents={{ click: (params: ECElementEvent) => props.onChartClick(params.name) }}
        />
    );
};
